import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { GoogleMap, Marker } from "@react-google-maps/api";

import { translationConstants, GoogleMapConstants } from "../../../constants";

const containerStyle = {
  width: "100%",
  height: "300px",
};

const FindOnMapModal = ({ setLng, setLat, lat, lng, handleSubmitLocation }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();
  const { t } = useTranslation();

  const [address, setAddress] = useState("");
  const [mapCenter, setMapCenter] = useState({
    lat: GoogleMapConstants.DEFAULT_LATITUDE,
    lng: GoogleMapConstants.DEFAULT_LONGITUDE,
  });
  const [markerPosition, setMarkerPosition] = useState(null);

  const handleSelect = (value) => {
    setAddress(value);
    geocodeByAddress(value)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) => {
        setValue("lat", lat);
        setValue("lng", lng);
        setLng(lng);
        setLat(lat);
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
      })
      .catch((error) => console.error("Error fetching coordinates: ", error));
  };

  const onSubmit = (data) => {
    handleSubmitLocation(data);
  };

  useEffect(() => {
    // Set initial form values from selected lat lng
    if (lat || lng) {
      setValue("lat", lat);
      setValue("lng", lng);
      setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
      setMarkerPosition({
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Update address when lat or lng changes
    const latValue = getValues("lat");
    const lngValue = getValues("lng");
    if (latValue && lngValue) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: parseFloat(latValue), lng: parseFloat(lngValue) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            setAddress(results[0].formatted_address);
          } else {
            console.error("Error fetching address: ", status);
          }
        }
      );
    }
  }, [getValues, lat, lng]);

  return (
    <div style={{ backgroundColor: "#EBEFE3" }}>
      <Form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="ViewUserContent p-5 px-0 px-md-5 mx-4"
      >
        <div className="ViewHeader">
          <p className="viewUserPara">{t(translationConstants.FIND_ON_MAP)}</p>
          <div className="d-flex"></div>
        </div>
        <p className="horizentalLine" />
        <Form.Group controlId="searchPlaces">
          <PlacesAutocomplete
            value={address}
            onChange={setAddress}
            onSelect={handleSelect}
          >
            {({
              getInputProps,
              suggestions,
              getSuggestionItemProps,
              loading,
            }) => (
              <div className="mb-2 mt-4 position-relative">
                <input
                  {...getInputProps({
                    placeholder: t(translationConstants.SEARCH_PLACES),
                  })}
                  className="form-control mb-2 focus-field-shadow"
                />
                <div className="suggestions-container">
                  {loading ? (
                    <div>{t(translationConstants.LOADING)}</div>
                  ) : null}
                  {suggestions.map((suggestion) => {
                    const style = {
                      backgroundColor: suggestion.active ? "#8ac675" : "#fff",
                      cursor: suggestion.active ? "pointer" : "default",
                    };
                    return (
                      <div
                        className="px-1"
                        {...getSuggestionItemProps(suggestion, { style })}
                        key={suggestion.placeId}
                      >
                        {suggestion.description}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </PlacesAutocomplete>
        </Form.Group>

        <div className="inputRow mb-2">
          <Form.Group controlId="longitude" className="mb-2">
            <Form.Label className="Form-labels">
              {t(translationConstants.ENTER_LONGITUDE)}
            </Form.Label>
            <Form.Control
              className="focus-field-shadow"
              type="text"
              placeholder={t(translationConstants.ENTER_LONGITUDE)}
              {...register("lng", {
                required: t(translationConstants.LNG_REQUIRED),
                pattern: {
                  value: /^-?((([1-9]?[0-9]|1[0-7][0-9])(\.\d+)?)|180(\.0+)?)$/,
                  message: t(translationConstants.LNG_RANGE_VALIDATION_TEXT),
                },
              })}
              onChange={(e) => {
                const value = e.target.value;
                setLng(value);
                setValue("lng", value);
                const latValue = getValues("lat");
                if (latValue) {
                  setMapCenter({ lat: parseFloat(latValue), lng: parseFloat(value) });
                  setMarkerPosition({
                    lat: parseFloat(latValue),
                    lng: parseFloat(value),
                  });
                }
              }}
            />
            {errors.lng && <p className="errorText">{errors.lng.message}</p>}
          </Form.Group>

          <Form.Group controlId="latitude" className="mb-2">
            <Form.Label className="Form-labels">
              {t(translationConstants.ENTER_LATITUDE)}
            </Form.Label>
            <Form.Control
              className="focus-field-shadow"
              type="text"
              placeholder={t(translationConstants.ENTER_LATITUDE)}
              {...register("lat", {
                required: t(translationConstants.LAT_REQUIRED),
                pattern: {
                  value: /^-?([1-8]?[0-9](\.\d+)?|90(\.0+)?)$/,
                  message: t(translationConstants.LAT_RANGE_VALIDATION_TEXT),
                },
              })}
              onChange={(e) => {
                const value = e.target.value;
                setLat(value);
                setValue("lat", value);
                const lngValue = getValues("lng");
                if (lngValue) {
                  setMapCenter({ lat: parseFloat(value), lng: parseFloat(lngValue) });
                  setMarkerPosition({
                    lat: parseFloat(value),
                    lng: parseFloat(lngValue),
                  });
                }
              }}
            />
            {errors.lat && <p className="errorText">{errors.lat.message}</p>}
          </Form.Group>
        </div>

        <GoogleMap
          key={`${mapCenter?.lat}-${mapCenter?.lng}`}
          mapContainerStyle={containerStyle}
          zoom={13}
          center={mapCenter}
          onClick={(e) => {
            const { lat, lng } = e.latLng.toJSON();
            setMarkerPosition({ lat, lng });
            setLng(lng);
            setLat(lat);
            setValue("lat", lat);
            setValue("lng", lng);
          }}
        >
          {markerPosition && (
            <Marker position={markerPosition} draggable={true} />
          )}
        </GoogleMap>

        <div className="row d-flex justify-content-center mt-4">
          <div className="col-md-4">
            <Button className="UpdateUserView-button w-100" type="submit">
              {t(translationConstants.submit)}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default FindOnMapModal;
